import { Fragment, type ReactNode } from 'react';
import { Alert, Typography } from 'antd';
import { polishAssistantReplyText } from '@/utils/aiAssistantTextSanitize';
import { AIChatBubble, AIResponseBlock } from '@/components/ai-design-system';
import type { AgentMessage } from '@/components/ai-agent/AgentMessages';

const { Paragraph, Text } = Typography;

function formatBoldLine(line: string): ReactNode {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, idx) => {
    if (p.startsWith('**') && p.endsWith('**') && p.length >= 4) {
      return (
        <Text strong key={idx}>
          {p.slice(2, -2)}
        </Text>
      );
    }
    return <Fragment key={idx}>{p}</Fragment>;
  });
}

function AssistantReplyBody({ text }: { text: string }) {
  const polished = polishAssistantReplyText(text);
  if (!polished) return <Text type="secondary">…</Text>;
  const blocks = polished.split(/\n\n+/);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split('\n');
        return (
          <Paragraph key={bi} style={{ marginBottom: bi < blocks.length - 1 ? 10 : 0, marginTop: 0 }}>
            {lines.map((line, li) => (
              <Fragment key={li}>
                {li > 0 ? <br /> : null}
                {formatBoldLine(line)}
              </Fragment>
            ))}
          </Paragraph>
        );
      })}
    </>
  );
}

function isRichData(data: unknown): boolean {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  const d = data as Record<string, unknown>;
  return Boolean(d.answerText || d.dataTable || d.chartSpec || d.kpiCards);
}

function intentHintFromData(data: unknown): string | undefined {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return undefined;
  const intent = (data as Record<string, unknown>).intent;
  return typeof intent === 'string' ? intent : undefined;
}

export interface AIChatThreadProps {
  messages: AgentMessage[];
  onActionPrompt?: (prompt: string) => void;
  onPin?: (input: {
    sourceType: 'kpi' | 'chart' | 'table' | 'report' | 'insight';
    title: string;
    payload: Record<string, unknown>;
  }) => void;
  onSaveUdq?: (input: {
    intent: string;
    filters?: Record<string, unknown>;
    query?: string;
    title?: string;
    displayColumns?: string[];
  }) => void;
}

export function AIChatThread({ messages, onActionPrompt, onPin, onSaveUdq }: AIChatThreadProps) {
  return (
    <div className="ai-workspace-chat-inner">
      {messages.map((m) => {
        if (m.role === 'user') {
          return (
            <AIChatBubble key={m.id} role="user">
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{m.text}</Paragraph>
            </AIChatBubble>
          );
        }
        if (m.role === 'assistant') {
          return (
            <AIChatBubble key={m.id} role="assistant">
              <AssistantReplyBody text={m.text || ''} />
            </AIChatBubble>
          );
        }
        if (m.role === 'error') {
          return <Alert key={m.id} type="error" showIcon message={m.text} style={{ marginBottom: 16 }} />;
        }
        return (
          <div key={m.id} data-intent={intentHintFromData(m.data)}>
            <AIResponseBlock
              toolName={m.toolName}
              data={m.data}
              onActionPrompt={onActionPrompt}
              onPin={onPin}
              onSaveUdq={onSaveUdq}
            />
          </div>
        );
      })}
    </div>
  );
}

export { isRichData };
